import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { PlayCircle, Lock, Film, Maximize2, Minimize2, Expand, Loader2, Check } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const VADOO_ALLOW = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen";

function LockedState({ signedIn }) {
  return (
    <div className="max-w-lg mx-auto text-center py-20" data-testid="library-locked">
      <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-5">
        <Lock className="w-7 h-7 text-gold" />
      </div>
      <h2 className="font-display text-2xl font-bold text-white mb-2">Members-only Video Library</h2>
      <p className="text-white/60 mb-6">
        {signedIn
          ? "This library is included with your Founding Membership. Become a member to watch."
          : "Sign in with your membership to watch the private video library."}
      </p>
      {signedIn ? (
        <Link to="/pricing" data-testid="library-upgrade-btn"
          className="inline-block px-5 py-2.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors">
          Become a Member
        </Link>
      ) : (
        <Link to="/login" data-testid="library-signin-btn"
          className="inline-block px-5 py-2.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors">
          Sign in
        </Link>
      )}
    </div>
  );
}

function Player({ active, theater, onToggleTheater }) {
  const wrapRef = useRef(null);
  const goFullscreen = () => {
    const el = wrapRef.current;
    if (el?.requestFullscreen) el.requestFullscreen();
    else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };
  return (
    <div className={`transition-all duration-300 ${theater ? "max-w-6xl" : "max-w-3xl"} mx-auto`} data-testid="library-player">
      <div ref={wrapRef} className="relative w-full bg-black rounded-lg overflow-hidden border border-white/10"
        style={{ aspectRatio: "16 / 9" }}>
        <iframe
          key={active.video_id}
          title="Video"
          src={active.embed_url}
          className="absolute inset-0 w-full h-full"
          style={{ border: 0 }}
          allow={VADOO_ALLOW}
          allowFullScreen
        />
      </div>
      <div className="flex items-center justify-between mt-3">
        <h3 className="font-display text-lg font-bold text-white pr-4 truncate" data-testid="library-now-playing">{active.title}</h3>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={onToggleTheater} data-testid="library-theater-btn"
            className="flex items-center gap-1.5 text-xs px-3 py-2 border border-white/15 rounded-sm text-white/70 hover:text-gold hover:border-gold/40 transition-colors">
            {theater ? <Minimize2 className="w-3.5 h-3.5" /> : <Expand className="w-3.5 h-3.5" />} {theater ? "Shrink" : "Theater"}
          </button>
          <button onClick={goFullscreen} data-testid="library-fullscreen-btn"
            className="flex items-center gap-1.5 text-xs px-3 py-2 border border-white/15 rounded-sm text-white/70 hover:text-gold hover:border-gold/40 transition-colors">
            <Maximize2 className="w-3.5 h-3.5" /> Fullscreen
          </button>
        </div>
      </div>
    </div>
  );
}

export default function VideoLibraryPage() {
  const { user } = useAuth();
  const isMember = !!user && (user.role === "admin" || user.subscription_status === "active");
  const [videos, setVideos] = useState(null);
  const [active, setActive] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [theater, setTheater] = useState(false);
  const [watched, setWatched] = useState(() => new Set());

  useEffect(() => {
    if (!isMember) return;
    api.get("/library/videos").then(({ data }) => setVideos(data)).catch(() => setVideos([]));
    api.get("/me/library-watches").then(({ data }) => setWatched(new Set(data.watched || []))).catch(() => {});
  }, [isMember]);

  const play = async (v) => {
    setLoadingId(v.video_id);
    try {
      const { data } = await api.get(`/library/videos/${v.video_id}/play`);
      setActive(data);
      setWatched((s) => new Set(s).add(v.video_id));
      setTheater(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      /* handled by empty state */
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal">
      <Helmet><title>Video Library · Help Solve The Case</title></Helmet>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-2 label-mono text-white/40 mb-1"><Film className="w-3.5 h-3.5" /> Members</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white mb-8">Video Library</h1>

        {!isMember ? (
          <LockedState signedIn={!!user} />
        ) : (
          <>
            {active && (
              <div className="mb-10">
                <Player active={active} theater={theater} onToggleTheater={() => setTheater((t) => !t)} />
              </div>
            )}

            {videos === null ? (
              <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading library…</div>
            ) : videos.length === 0 ? (
              <div className="text-center text-white/50 py-16" data-testid="library-empty">No videos published yet. Check back soon.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-list">
                {videos.map((v) => {
                  const isActive = active?.video_id === v.video_id;
                  return (
                    <motion.button
                      key={v.video_id}
                      onClick={() => play(v)}
                      disabled={loadingId === v.video_id}
                      data-testid={`library-video-${v.video_id}`}
                      whileHover={{ y: -3 }}
                      className={`group text-left rounded-lg overflow-hidden border card-surface transition-colors ${isActive ? "border-gold" : "border-white/10 hover:border-gold/40"}`}
                    >
                      <div className="relative w-full bg-black/60 overflow-hidden" style={{ aspectRatio: "16 / 9" }}>
                        {v.thumbnail
                          ? <img src={v.thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                          : <div className="absolute inset-0 flex items-center justify-center"><Film className="w-8 h-8 text-white/20" /></div>}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                          {loadingId === v.video_id
                            ? <Loader2 className="w-9 h-9 text-gold animate-spin" />
                            : <PlayCircle className="w-11 h-11 text-white/90 group-hover:text-gold transition-colors" />}
                        </div>
                      </div>
                      <div className="p-4">
                        {watched.has(v.video_id) && (
                          <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-white/15 bg-white/[0.04] text-white/50 mb-1.5" data-testid={`library-watched-${v.video_id}`}>
                            <Check className="w-2.5 h-2.5" /> Watched
                          </span>
                        )}
                        <h3 className="font-semibold text-white leading-snug line-clamp-2">{v.title}</h3>
                        {v.description && <p className="mt-1.5 text-sm text-white/55 line-clamp-2">{v.description}</p>}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
